import { Histogram, Meter, metrics } from "@opentelemetry/api";
import { List, Map, Record } from "immutable";
import { Metric, onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";

const Vital: Record.Factory<{
  metricName: string;
  unit: string;
  boundaries: List<number>;
  subscribe: (callback: (metric: Metric) => void) => void;
  untilPageHide: boolean;
}> = Record({
  // OneUptime matches vitals on this exact name; any other spelling populates nothing and reports no error.
  metricName: "",
  unit: "ms",
  boundaries: List<number>(),
  subscribe: onLCP as (callback: (metric: Metric) => void) => void,
  // web-vitals re-reports CLS and INP on every hide where they changed, so only the value at pagehide is recorded.
  untilPageHide: false,
});

const VITALS = Map({
  LCP: Vital({ metricName: "web_vital.lcp", boundaries: List([500, 1000, 1500, 2000, 2500, 3000, 4000, 5000, 7500, 10000]), subscribe: onLCP }),
  INP: Vital({ metricName: "web_vital.inp", boundaries: List([25, 50, 75, 100, 150, 200, 300, 400, 500, 750, 1000]), subscribe: onINP, untilPageHide: true }),
  CLS: Vital({ metricName: "web_vital.cls", unit: "1", boundaries: List([0.01, 0.025, 0.05, 0.1, 0.15, 0.25, 0.5, 1]), subscribe: onCLS, untilPageHide: true }),
  FCP: Vital({ metricName: "web_vital.fcp", boundaries: List([250, 500, 750, 1000, 1500, 1800, 2500, 3000, 4000, 6000]), subscribe: onFCP }),
  TTFB: Vital({ metricName: "web_vital.ttfb", boundaries: List([100, 200, 400, 600, 800, 1000, 1400, 1800, 2500, 4000]), subscribe: onTTFB }),
});

export const reportWebVitals = (): void => {
  const meter: Meter = metrics.getMeter("lims-web-vitals");
  // The latest pending recording per metric.id, so a value re-reported on each hide is recorded once.
  let pending: Map<string, () => void> = Map();

  VITALS.forEach((vital, name): void => {
    const histogram: Histogram = meter.createHistogram(vital.metricName, {
      unit: vital.unit,
      description: `Core Web Vital: ${name}`,
      advice: { explicitBucketBoundaries: vital.boundaries.toArray() },
    });
    vital.subscribe((metric: Metric): void => {
      const record = (): void => histogram.record(metric.value, { "web_vital.rating": metric.rating });
      if (vital.untilPageHide) {
        pending = pending.set(metric.id, record);
      } else {
        record();
      }
    });
  });

  addEventListener("pagehide", (): void => {
    pending.forEach((record: () => void): void => record());
    pending = Map();
  });
};
